import { useEffect, useState } from "react";
import { Button } from "@tremor/react";
import { toast } from "sonner";
import {
  approveVisualBaseline,
  fetchVisualBaseline,
  rejectVisualBaseline,
  resetVisualBaseline,
} from "../../api/monitors.js";

function Preview({ title, src, empty }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {title}
      </p>
      {src ? (
        <img
          src={src}
          alt={title}
          className="h-64 w-full rounded border border-gray-100 object-contain bg-gray-50"
        />
      ) : (
        <div className="flex h-64 w-full items-center justify-center rounded border border-dashed border-gray-200 bg-gray-50 text-sm text-gray-400">
          {empty}
        </div>
      )}
    </div>
  );
}

function TextDiff({ added, removed }) {
  if (!added?.length && !removed?.length) {
    return (
      <p className="text-sm text-gray-500">No text changes detected.</p>
    );
  }
  return (
    <div className="space-y-2 font-mono text-xs">
      {removed?.length > 0 && (
        <div className="rounded border border-red-200 bg-red-50 p-2">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-red-700">
            Removed
          </p>
          {removed.map((line, i) => (
            <div key={`r-${i}`} className="text-red-800">- {line}</div>
          ))}
        </div>
      )}
      {added?.length > 0 && (
        <div className="rounded border border-emerald-200 bg-emerald-50 p-2">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-emerald-700">
            Added
          </p>
          {added.map((line, i) => (
            <div key={`a-${i}`} className="text-emerald-800">+ {line}</div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function VisualChangePanel({ monitor, latestCheckResult }) {
  const [baseline, setBaseline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      setBaseline(await fetchVisualBaseline(monitor.id));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [monitor.id]);

  async function approve() {
    setBusy(true);
    try {
      await approveVisualBaseline(monitor.id);
      toast.success("Baseline updated to the latest capture.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Approval failed");
    } finally {
      setBusy(false);
    }
  }

  async function reject() {
    setBusy(true);
    try {
      await rejectVisualBaseline(monitor.id);
      toast.success("Pending change discarded.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Reject failed");
    } finally {
      setBusy(false);
    }
  }

  async function reset() {
    if (!confirm("Reset the baseline? The next check will capture a fresh one.")) return;
    setBusy(true);
    try {
      await resetVisualBaseline(monitor.id);
      toast.success("Baseline cleared.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Reset failed");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <p className="text-sm text-gray-500">Loading baseline…</p>;
  }

  const latestData = latestCheckResult?.data || {};
  // Presigned S3 URLs come from the API — same pattern as HAR/screenshots —
  // so the browser can load them via plain <img> tags without our JWT.
  const baselineImg = baseline?.screenshot_url || null;
  const pendingImg =
    baseline?.pending_screenshot_url ||
    latestData.latest_screenshot_url ||
    null;
  const diffImg = latestData.diff_screenshot_url || null;

  return (
    <div className="space-y-4">
      {!baseline && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          No baseline yet. The first successful check will capture one.
        </div>
      )}

      {baseline?.approval_pending && (
        <div className="rounded-lg border border-teal-200 bg-teal-50 p-4">
          <p className="text-sm font-semibold text-teal-800">
            Change pending approval
            {baseline.pending_change_percent != null
              ? ` — ${baseline.pending_change_percent.toFixed(2)}% pixels changed`
              : ""}
          </p>
          <p className="mt-1 text-xs text-teal-700">
            Approve to make this the new baseline, or reject to keep the current
            baseline and continue alerting if the change persists.
          </p>
          <div className="mt-3 flex gap-2">
            <Button onClick={approve} disabled={busy}>Approve as new baseline</Button>
            <Button variant="secondary" onClick={reject} disabled={busy}>Reject</Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Preview
          title="Baseline"
          src={baselineImg}
          empty={baseline ? "No image stored" : "Waiting for first capture"}
        />
        <Preview
          title="Latest"
          src={pendingImg /* prefer pending when present so the diff is meaningful */}
          empty="No pending capture"
        />
        <Preview
          title="Diff"
          src={diffImg}
          empty="No diff available"
        />
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          Latest check
        </p>
        <dl className="grid grid-cols-2 gap-y-1 text-sm text-gray-700">
          <dt className="text-gray-500">Change</dt>
          <dd>{latestData.change_percent != null
            ? `${Number(latestData.change_percent).toFixed(2)}%`
            : "—"}</dd>
          <dt className="text-gray-500">Threshold</dt>
          <dd>{latestData.threshold_percent ?? "—"}%</dd>
          <dt className="text-gray-500">Mode</dt>
          <dd>{latestData.mode ?? "—"}</dd>
          <dt className="text-gray-500">Reason</dt>
          <dd>{latestData.change_reason ?? "No change"}</dd>
        </dl>
        {latestData.keyword_results && (
          <div className="mt-3">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Keywords
            </p>
            <ul className="text-sm">
              {Object.entries(latestData.keyword_results).map(([kw, present]) => (
                <li key={kw}>
                  <span className={present ? "text-emerald-700" : "text-red-700"}>
                    {present ? "✓" : "✗"}
                  </span>{" "}
                  <span className="font-mono">{kw}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          Text diff
        </p>
        <TextDiff added={latestData.text_added} removed={latestData.text_removed} />
      </div>

      <div className="flex justify-end">
        <Button variant="secondary" onClick={reset} disabled={busy || !baseline}>
          Reset baseline
        </Button>
      </div>
    </div>
  );
}
